import { RefObject, useLayoutEffect, useState } from 'react';

function elementIntersectsViewport(el: Element): boolean {
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return rect.bottom > 0 && rect.top < vh && rect.right > 0 && rect.left < vw;
}

/**
 * Tracks viewport intersection for a sentinel element. Performs a synchronous
 * layout measurement in useLayoutEffect (before paint) so client navigations do
 * not flash incorrect state from hooks that default to “not intersecting” until
 * useEffect runs.
 */
export function useSentinelInView(
  ref: RefObject<Element | null>,
  /** When this changes, visibility is re-measured (e.g. product slug swap). */
  _layoutKey?: string,
): boolean {
  const [isIntersecting, setIsIntersecting] = useState(true);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    setIsIntersecting(elementIntersectsViewport(el));

    const observer = new IntersectionObserver(([entry]) => {
      if (entry) setIsIntersecting(entry.isIntersecting);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return isIntersecting;
}
